import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import api from '../lib/api';
import { toast } from 'sonner';
import { Building2, Plus, AlertTriangle, Search, X } from 'lucide-react';

const STATUS_BADGE = {
  aktiv: { text: 'Aktiv', bg: '#dcfce7', color: '#166534' },
  inaktiv: { text: 'Inaktiv', bg: '#f3f4f6', color: '#374151' },
  uppsagd: { text: 'Uppsagd', bg: '#fee2e2', color: '#991b1b' },
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('sv-SE');
}

// ── Skapa-modal ────────────────────────────────────────────────────────────────

const CreateCustomerModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: '', org_number: '', contact_person: '', email: '', phone: '',
    street_address: '', postal_code: '', city: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const setField = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const resp = await api.post('/admin/customers', form);
      toast.success('Kundorganisation skapad');
      onCreated(resp.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skapa kund');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.25)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Ny kundorganisation</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-sm font-semibold mb-1">Namn på organisation *</label>
            <input
              required
              type="text"
              value={form.name}
              onChange={setField('name')}
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Organisationsnummer * (XXXXXX-XXXX)</label>
            <input
              required
              type="text"
              value={form.org_number}
              onChange={setField('org_number')}
              pattern="\d{6}-\d{4}"
              placeholder="556123-4567"
              className="w-full border rounded px-3 py-2 text-sm font-mono"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Kontaktperson *</label>
            <input
              required
              type="text"
              value={form.contact_person}
              onChange={setField('contact_person')}
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-1">E-post *</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={setField('email')}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Telefon</label>
              <input
                type="text"
                value={form.phone}
                onChange={setField('phone')}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Gatuadress</label>
            <input
              type="text"
              value={form.street_address}
              onChange={setField('street_address')}
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold mb-1">Postnummer</label>
              <input
                type="text"
                value={form.postal_code}
                onChange={setField('postal_code')}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Ort</label>
              <input
                type="text"
                value={form.city}
                onChange={setField('city')}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Avbryt
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded text-black font-semibold disabled:opacity-50"
              style={{ background: '#F9CF3F' }}
            >
              {submitting ? 'Sparar...' : 'Skapa kundorganisation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Efter-skapa-prompt ────────────────────────────────────────────────────────

const PostCreatePrompt = ({ customer, onClose, onInviteAdmin }) => {
  const navigate = useNavigate();
  if (!customer) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.25)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold mb-2">Kundorganisation skapad</h2>
        <p className="text-sm text-gray-700 mb-4">
          {customer.name} är nu tillagd. Vad vill du göra nu?
        </p>
        <div className="space-y-2">
          <button
            onClick={() => {
              navigate(`/admin/contracts/new?customer_id=${customer.id}`);
              onClose();
            }}
            className="w-full px-4 py-2 rounded font-semibold text-black hover:opacity-90"
            style={{ background: '#F9CF3F' }}
          >
            Skapa avtal nu
          </button>
          <button
            onClick={() => {
              onInviteAdmin(customer);
              onClose();
            }}
            className="w-full px-4 py-2 rounded font-semibold border hover:bg-gray-50"
          >
            Bjud in administratör
          </button>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded"
          >
            Avsluta
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Bjud in admin-modal ────────────────────────────────────────────────────────

const InviteAdminModal = ({ customer, onClose, onInvited }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!customer) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/admin/customers/${customer.id}/invite-admin`, { email, name });
      toast.success(`Inbjudan skickad till ${email}`);
      onInvited();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte bjuda in admin');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.25)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Bjud in administratör</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Användaren får ett mejl med temporärt lösenord och blir customer_admin för <strong>{customer.name}</strong>.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-sm font-semibold mb-1">Namn *</label>
            <input
              required
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">E-post *</label>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Avbryt
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded text-black font-semibold disabled:opacity-50"
              style={{ background: '#F9CF3F' }}
            >
              {submitting ? 'Skickar...' : 'Skicka inbjudan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Huvud-sida ────────────────────────────────────────────────────────────────

const CustomersPage = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [postCreate, setPostCreate] = useState(null);
  const [inviteFor, setInviteFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/customers');
      setCustomers(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error('Kunde inte hämta kundorganisationer');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = customers.filter((c) => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (c.name || '').toLowerCase().includes(q)
        || (c.org_number || '').toLowerCase().includes(q)
        || (c.email || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="customers-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Building2 size={24} />
              Kundorganisationer
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Hantera kundorganisationer som använder AIUS Control
            </p>
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="new-customer-btn"
            className="flex items-center gap-1 px-4 py-2 rounded font-semibold text-black"
            style={{ background: '#F9CF3F' }}
          >
            <Plus size={16} />
            Ny kundorganisation
          </button>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Sök på namn, org.nr eller e-post..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border rounded text-sm"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border rounded text-sm"
          >
            <option value="all">Alla statusar</option>
            <option value="aktiv">Aktiva</option>
            <option value="inaktiv">Inaktiva</option>
            <option value="uppsagd">Uppsagda</option>
          </select>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Laddar...</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500 bg-white rounded border">
            {customers.length === 0
              ? 'Inga kundorganisationer ännu. Skapa den första!'
              : 'Inga kunder matchar filtret.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((c) => {
              const status = STATUS_BADGE[c.status] || STATUS_BADGE.aktiv;
              return (
                <div
                  key={c.id}
                  className="bg-white rounded border p-4 hover:shadow transition"
                  data-testid={`customer-card-${c.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <button
                        onClick={() => navigate(`/admin/customers/${c.id}`)}
                        className="text-lg font-bold text-left hover:underline"
                      >
                        {c.name}
                      </button>
                      <p className="text-xs text-gray-500 font-mono mt-0.5">
                        {c.org_number || 'inget org.nr'}
                      </p>
                    </div>
                    <span
                      className="px-2 py-0.5 rounded text-xs font-semibold"
                      style={{ background: status.bg, color: status.color }}
                    >
                      {status.text}
                    </span>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-xs text-gray-500">Användare</p>
                      <p className="font-semibold">{c.active_users_count}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Deltagare</p>
                      <p className="font-semibold">{c.active_participants_count}</p>
                    </div>
                  </div>

                  {c.warnings && c.warnings.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {c.warnings.map((w) => (
                        <button
                          key={w}
                          onClick={() => {
                            if (w === 'saknar avtal') {
                              navigate(`/admin/contracts/new?customer_id=${c.id}`);
                            } else if (w === 'saknar administratör') {
                              setInviteFor(c);
                            }
                          }}
                          className="flex items-center gap-1 text-xs font-semibold rounded px-2 py-1 hover:opacity-80"
                          style={{ background: '#fff7ed', color: '#9a3412' }}
                        >
                          <AlertTriangle size={12} />
                          {w}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t text-xs text-gray-500">
                    <p>
                      <span className="font-semibold">{c.contact_person || '—'}</span> · {c.email}
                    </p>
                    <p>Skapad: {formatDate(c.created_at)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <CreateCustomerModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={(c) => { setPostCreate(c); load(); }}
        />
        <PostCreatePrompt
          customer={postCreate}
          onClose={() => setPostCreate(null)}
          onInviteAdmin={(c) => setInviteFor(c)}
        />
        <InviteAdminModal
          customer={inviteFor}
          onClose={() => setInviteFor(null)}
          onInvited={load}
        />
      </div>
    </DashboardLayout>
  );
};

export default CustomersPage;
